import { useState, useEffect, useRef, useCallback } from "react";
import { Flex, ConfigProvider } from "antd";
import { Bubble, Sender, Prompts, Conversations, Welcome, XProvider } from "@ant-design/x";
import FeedbackV2 from "Components/FeedbackV2/FeedbackV2";
import SbSuccessAlert from "Components/SbAlertComponent/SbSuccessAlert";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useLocation, useNavigate } from "react-router-dom";
import SbDrawer from "uiCore/SbDrawer/SbDrawer";
import {
  AskAllieFabStyled,
  COMPACT_DRAWER_STYLES,
  DRAWER_BODY_STYLES,
} from "uiCore/AskAllie.styles";
import {
  UserOutlined,
  ArrowLeftOutlined,
  CloseOutlined,
  HistoryOutlined,
  MinusOutlined,
  FullscreenOutlined,
  PlusOutlined,
  FlagOutlined,
  MessageOutlined,
  DeleteOutlined,
} from "uiCore/SbIcons/SbIcons";
import SbUpload from "uiCore/SbUpload/SbUpload";
import SbAvatar from "uiCore/SbAvatar/SbAvatar";
import {
  SbTag,
  SbBetaTag,
  SbTooltip,
  SbNotification,
  SbButton,
  BUTTON_TYPE,
  SbInput,
  SbBadge,
  SbTabs,
  SbSpin,
} from "uiCore";
import FileuploadIcon from "Components/SBLayoutIcon/FileuploadIcon";
import StarsIcon from "Components/SBLayoutIcon/StarsIcon";
import ThumbIconUp from "Components/SBLayoutIcon/ThumbIconUp";
import ThumbIconDown from "Components/SBLayoutIcon/ThumbIconDown";
import { SB_COLORS } from "theme/SbThemeV2";
import useAuthStore from "Store/useAuthStore";
import useEntitlementsStore from "Store/useEntitlementsStore";
import useAllieStore, {
  PANEL_SIZE,
  PANEL_WIDTH,
  PANEL_VIEW,
  PANEL_TAB,
} from "Store/useAllieStore";
import { DraggableCore } from "react-draggable";
import useAllieDrag from "Hooks/useAllieDrag";
import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
import useAllieFocusPriority from "Hooks/useAllieFocusPriority";
import { callAllieBackend, submitAllieFeedback, submitUserFeedback, fetchAllieHistory } from "Api/apiService";
import sanitizeMarkdown from "Utils/sanitizeMarkdown";

import { resolvePageContext, resolveSuggestions, CAPABILITIES_INTRO } from "Components/SbLayoutComponent/SbAssistConfig";
import {
  nextId,
  generateConvId,
  generateTitle,
  groupByDate,
  personalizeGreeting,
  upsertConversation,
} from "Utils/askAllie";

import "Components/SbLayoutComponent/Ask_allie.scss";

const ALLIE_ANALYTICS_FROM = "ask_allie";
const ALLIE_EVENTS = ANALYTICS_EVENTS.ASK_ALLIE;

// Links in Allie's answers come from the knowledge base: in-app routes navigate
// via the router, everything else opens in a new tab.
const AssistantMarkdownLink = ({ href, children, ...props }) => {
  const navigate = useNavigate();
  const minimizePanel = useAllieStore((state) => state.minimizePanel);
  const isInternal = typeof href === "string" && href.startsWith("/");

  if (isInternal) {
    return (
      <a
        href={href}
        {...props}
        onClick={(e) => {
          e.preventDefault();
          // Dock the panel so it stops covering the page the user asked for,
          // without ending the conversation.
          minimizePanel();
          navigate(href);
        }}
      >
        {children}
      </a>
    );
  }

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  );
};

const AssistantMarkdown = ({ content }) => (
  <div className="sb-markdown-content">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: AssistantMarkdownLink }}>
      {sanitizeMarkdown(content || "")}
    </ReactMarkdown>
  </div>
);

// Avatar + placement + bubble look per role, defined once for Bubble.List.
const BUBBLE_ROLES = {
  assistant: {
    placement: "start",
    variant: "outlined",
    avatar: <SbAvatar icon={<StarsIcon />} style={{ backgroundColor: SB_COLORS.secondary, color: SB_COLORS.primary }} />,
    contentRender: (content) => <AssistantMarkdown content={content} />,
  },
  user: {
    placement: "end",
    variant: "filled",
    avatar: <SbAvatar icon={<UserOutlined />} style={{ backgroundColor: SB_COLORS.primary, color: "#fff" }} />,
  },
};

// ─── Feedback attachments: screenshots and short screen recordings ─────────────
const FEEDBACK_MEDIA_TYPES = [
  "image/jpeg", "image/png", "image/webp", "image/gif",
  "video/mp4", "video/webm", "video/quicktime",
];
const FEEDBACK_ACCEPT = FEEDBACK_MEDIA_TYPES.join(",");
const FEEDBACK_MAX_SIZE_MB = 10; // per-file cap for screenshots/short recordings
const FEEDBACK_MAX_SIZE_BYTES = FEEDBACK_MAX_SIZE_MB * 1024 * 1024; // file.size is in bytes
const FEEDBACK_MAX_FILES = 5; // max attachments per thumbs-down feedback

// ─── Component ────────────────────────────────────────────────────────────────
const AskAllie = () => {
  const location = useLocation();
  const profileName = useAuthStore((state) => state.profileName);
  const profileRole = useEntitlementsStore((state) => state.profileRole);
  const entitlements = useEntitlementsStore((state) => state.entitlements);

  // ── Panel state (Zustand — shared with the markdown link renderer) ──────────
  const show = useAllieStore((state) => state.show);
  const panelSize = useAllieStore((state) => state.panelSize);
  const view = useAllieStore((state) => state.view);
  const activeTab = useAllieStore((state) => state.activeTab);
  const openPanel = useAllieStore((state) => state.openPanel);
  const closePanel = useAllieStore((state) => state.closePanel);
  const minimizePanel = useAllieStore((state) => state.minimizePanel);
  const expandPanel = useAllieStore((state) => state.expandPanel);
  const setView = useAllieStore((state) => state.setView);
  const setActiveTab = useAllieStore((state) => state.setActiveTab);
  const isCompact = panelSize === PANEL_SIZE.COMPACT;

  const track = useAnalytics(ALLIE_ANALYTICS_FROM);

  // Open/close is the top-of-funnel number for Allie, and the panel is global
  // — it has no route of its own, so the automatic Mixpanel pageview never
  // captures it. `pageContext` records which screen it was opened over.
  useEffect(() => {
    if (!show) return;
    track(ALLIE_EVENTS.PANEL_OPENED, {
      pageContext: pageContext.displayName || location.pathname,
      panelSize,
    });
    // Only on open — panelSize changes have their own event below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (!show) return;
    track(ALLIE_EVENTS.PANEL_MODE_CHANGED, { panelSize, view });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelSize, view]);

  // ── Drag-to-move ────────────────────────────────────────────────────────────
  // Works at both sizes. The compact card is already floating, so a drag just
  // translates it; the full panel is docked full-height, so `undockOnDrag` turns it
  // into a movable window on grab. Size changes reset the position, since each size
  // anchors the panel somewhere different.
  const {
    draggableProps,
    dragHandleProps,
    wrapperStyle: dragWrapperStyle,
    isUndocked,
    isDragging,
  } = useAllieDrag({ enabled: show, undockOnDrag: !isCompact, resetKey: panelSize });
  const headerRef = useRef(null);

  // ── Typing while another overlay is open ────────────────────────────────────
  // Masked drawers/modals trap focus and would pull every keystroke out of Allie.
  // This claims antd's own focus lock while the user is in Allie — see the hook.
  const focusTrap = useAllieFocusPriority({ enabled: show });

  // Once moved, Allie is a window over the page rather than a panel beside it —
  // let clicks through everywhere except the panel itself, the same trick the
  // compact card already uses.
  const drawerStyles = (() => {
    const base = isCompact ? COMPACT_DRAWER_STYLES : DRAWER_BODY_STYLES;
    if (!dragWrapperStyle) return base;
    return {
      ...base,
      root: { ...base.root, pointerEvents: "none" },
      wrapper: { ...base.wrapper, pointerEvents: "auto", ...dragWrapperStyle },
    };
  })();

  // ── Chat state ───────────────────────────────────────────────────────────────
  const [messages, setMessages] = useState([]); // active conversation
  const [conversations, setConversations] = useState([]); // saved history
  const [inputValue, setInputValue] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);

  // ── Feedback state ───────────────────────────────────────────────────────────
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackFiles, setFeedbackFiles] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [showFeedbackSuccess, setShowFeedbackSuccess] = useState(false);

  // ── Thumbs-down inline reason state ─────────────────────────────────────────
  const [downPendingMsgId, setDownPendingMsgId] = useState(null); // msgId awaiting reason
  const [downReasonText, setDownReasonText] = useState("");
  const [downFiles, setDownFiles] = useState([]); // screenshots/videos for this thumbs-down

  // ── History loading state ────────────────────────────────────────────────────
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  // ── Refs ────────────────────────────────────────────────────────────────────
  const inputRef = useRef(null);
  const bubbleListRef = useRef(null); // Bubble.List imperative handle (scrollTo)
  const allieRequestRef = useRef(null); // holds { abort } from callAllieBackend

  // ── Derived values ───────────────────────────────────────────────────────────
  const pageContext = resolvePageContext(location.pathname);
  const suggestions = resolveSuggestions(location.pathname);
  // Chips wrap onto several rows and would crowd out the compact card.
  const showSuggestions = messages.length === 1 && !isStreaming && !isCompact;
  // A chat the user has actually spoken in — the greeting alone doesn't count.
  const hasActiveConversation = messages.some((m) => m.role === "user");

  // ─────────────────────────────────────────────────────────────────────────────
  // Effects
  // ─────────────────────────────────────────────────────────────────────────────

  // Focus the input when the chat view is visible
  useEffect(() => {
    if (show && view === PANEL_VIEW.CHAT) {
      const t = setTimeout(() => inputRef.current?.focus(), 350);
      return () => clearTimeout(t);
    }
  }, [show, view]);

  // Abort any in-flight Allie request on unmount
  useEffect(() => {
    return () => allieRequestRef.current?.abort();
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // History helpers
  // ─────────────────────────────────────────────────────────────────────────────

  /** Snapshot the active chat, keyed by its backend session id so the
   *  fetchAllieHistory merge recognizes it as the same conversation. */
  const snapshotCurrent = useCallback(() => {
    if (!messages.some((m) => m.role === "user")) return null;
    return {
      id: currentSessionId || generateConvId(),
      title: generateTitle(messages),
      messages: [...messages],
      createdAt: Date.now(),
    };
  }, [messages, currentSessionId]);

  /** Persist the current chat to history if it has at least one user message. */
  const saveCurrentToHistory = useCallback(() => {
    const snapshot = snapshotCurrent();
    if (!snapshot) return;
    setConversations((prev) => upsertConversation(prev, snapshot));
  }, [snapshotCurrent]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Open / Close
  // ─────────────────────────────────────────────────────────────────────────────

  const handleOpen = () => {
    openPanel();
    // Reopening returns the user to exactly where they left off; only a fresh
    // open resets the view and greets.
    if (messages.length === 0) {
      setView(PANEL_VIEW.CHAT);
      setActiveTab(PANEL_TAB.CHAT);
      setMessages([
        {
          id: nextId(),
          role: "assistant",
          content: personalizeGreeting(pageContext.greeting, profileName) + CAPABILITIES_INTRO,
          isStreaming: false,
          isTyping: false,
        },
      ]);
    }
  };

  // Alt+A (Option+A on macOS) cycles closed → full → compact → full. Toggling size rather than
  // closing means the shortcut never discards an in-flight reply.
  useEffect(() => {
    const handler = (e) => {
      // Match on e.code (physical key): macOS Option+A composes e.key into 'å',
      // so an e.key === 'a' check never fires there.
      if (e.altKey && !e.ctrlKey && !e.metaKey && e.code === 'KeyA') {
        e.preventDefault();
        if (!show) handleOpen();
        else if (isCompact) expandPanel();
        else minimizePanel();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [show, isCompact, minimizePanel, expandPanel]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    track(ALLIE_EVENTS.PANEL_CLOSED, { messageCount: messages.length });
    allieRequestRef.current?.abort();
    setIsStreaming(false);
    // Keep currentSessionId: closing does not clear `messages`, and dropping the
    // id makes the next send open a second backend session that replays the same
    // turns. Use "New conversation" to start fresh.
    closePanel();
    setFeedbackText("");
    setFeedbackFiles([]);
    setShowFeedbackSuccess(false);
  };

  const handleFeedbackSubmit = async ({ feedback_type, comment, files }) => {
    // The free-text comment is not sent, for the same reason the question
    // isn't — only the shape of the feedback.
    track(ALLIE_EVENTS.FEEDBACK_SUBMITTED, {
      scope: "panel",
      feedbackType: feedback_type,
      hasComment: !!comment?.trim(),
      fileCount: files?.length ?? 0,
    });
    setFeedbackLoading(true);
    try {
      let response;
      if (files?.length > 0) {
        const formData = new FormData();
        formData.append("feedback_type", feedback_type ?? "");
        formData.append("feedback_text", comment);
        formData.append("uri_at_time_of_feedback", location.pathname);
        files.forEach((file) => formData.append("files", file));
        response = await submitUserFeedback(formData);
      } else {
        response = await submitUserFeedback({
          feedback_type: feedback_type ?? null,
          feedback_text: comment,
          uri_at_time_of_feedback: location.pathname,
          sb_unique_id: null,
        });
      }
      if (response?.message) {
        setFeedbackText("");
        setFeedbackFiles([]);
        setShowFeedbackSuccess(true);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error submitting feedback:", err);
    } finally {
      setFeedbackLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // New Conversation
  // ─────────────────────────────────────────────────────────────────────────────

  const handleNewChat = useCallback(() => {
    track(ALLIE_EVENTS.CHAT_CLEARED, { messageCount: messages.length });
    allieRequestRef.current?.abort();
    setIsStreaming(false);
    setCurrentSessionId(null);
    saveCurrentToHistory();
    setInputValue("");
    setMessages([
      {
        id: nextId(),
        role: "assistant",
        content: personalizeGreeting(pageContext.greeting, profileName) + CAPABILITIES_INTRO,
        isStreaming: false,
        isTyping: false,
      },
    ]);
    setView(PANEL_VIEW.CHAT);
  }, [saveCurrentToHistory, pageContext.greeting, setView, track]);

  // ─────────────────────────────────────────────────────────────────────────────
  // History navigation
  // ─────────────────────────────────────────────────────────────────────────────

  const handleOpenHistory = useCallback(async () => {
    // Snapshot first, keyed by the real session id, so the API copy fetched
    // below replaces it instead of sitting beside it.
    saveCurrentToHistory();

    setView(PANEL_VIEW.HISTORY);
    setIsHistoryLoading(true);
    try {
      const data = await fetchAllieHistory();
      const apiConvs = (data.sessions || []).map((s) => {
        const msgs = (s.messages || []).map((m) => ({
          id: m.message_id,
          role: m.role === "model" ? "assistant" : m.role,
          content: m.content,
          isStreaming: false,
          isTyping: false,
        }));
        const rawTs = new Date(s.last_active || s.created_at).getTime();
        return {
          id: s.session_id,
          title: generateTitle(msgs),
          messages: msgs,
          createdAt: isNaN(rawTs) ? Date.now() : rawTs,
        };
      });

      // API sessions win; keep local-only entries not yet flushed to the backend.
      setConversations((localConvs) => {
        const apiIds = new Set(apiConvs.map((c) => c.id));
        const localOnly = localConvs.filter((c) => !apiIds.has(c.id));
        return [...apiConvs, ...localOnly].sort((a, b) => b.createdAt - a.createdAt);
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to load chat history:", err);
      // Keep whatever local conversations are already in state.
    } finally {
      setIsHistoryLoading(false);
    }
  }, [saveCurrentToHistory, setView]);

  /** Load a saved conversation into the active chat, auto-saving the current one. */
  const handleLoadConversation = useCallback(
    (convId) => {
      const target = conversations.find((c) => c.id === convId);
      if (!target) return;
      if (convId === currentSessionId) {
        // Already the active chat; re-snapshotting would duplicate it.
        setView(PANEL_VIEW.CHAT);
        return;
      }

      // Save the outgoing chat under its own session id. The selected
      // conversation stays in history — it is the same record the backend
      // returns on the next fetch.
      const outgoing = snapshotCurrent();
      if (outgoing && outgoing.id !== convId) {
        setConversations((prev) => upsertConversation(prev, outgoing));
      }

      allieRequestRef.current?.abort();
      setIsStreaming(false);
      setMessages(target.messages);
      setCurrentSessionId(target.id);
      setInputValue("");
      setView(PANEL_VIEW.CHAT);
    },
    [conversations, currentSessionId, snapshotCurrent, setView]
  );

  const handleDeleteConversation = useCallback(
    (convId) => setConversations((prev) => prev.filter((c) => c.id !== convId)),
    []
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Send
  // ─────────────────────────────────────────────────────────────────────────────

  /** Core send — shared by the text input and suggestion chips. */
  const sendMessage = useCallback(
    (text) => {
      if (!text.trim() || isStreaming) return;

      // The question itself is deliberately NOT sent. Every other module sends
      // its search term, but an Allie prompt is free text a user may paste
      // employee or salary details into — length and page context answer
      // "is Allie being used, and where" without carrying that risk.
      track(ALLIE_EVENTS.QUESTION_SUBMITTED, {
        pageContext: pageContext.displayName || location.pathname,
        messageLength: text.trim().length,
        turnIndex: messages.length,
      });

      const userMsgId = nextId();
      const botMsgId = nextId();

      // Snapshot history before appending the new user turn
      const historyForBackend = messages
        .filter((m) => !m.isTyping && m.content)
        .map((m) => ({
          role: m.role === "assistant" ? "model" : "user",
          content: m.content,
        }));

      setMessages((prev) => [
        ...prev,
        {
          id: userMsgId,
          role: "user",
          content: text.trim(),
          isStreaming: false,
          isTyping: false,
        },
      ]);

      setIsStreaming(true);
      setMessages((prev) => [
        ...prev,
        { id: botMsgId, role: "assistant", content: "", isStreaming: true, isTyping: true },
      ]);

      // Pin the just-sent question to the top so the reply streams in below it.
      setTimeout(() => {
        bubbleListRef.current?.scrollTo({ key: userMsgId, block: "start" });
      }, 50);

      // Modules the currently active (switched) role can see. The backend
      // intersects these with the JWT entitlements, so this can only narrow
      // Allie's knowledge access — never escalate it.
      const activeRoleResources = Array.isArray(entitlements)
        ? entitlements.map((r) => r?.name).filter(Boolean)
        : [];

      const payload = {
        message: text.trim(),
        pageContext: pageContext.displayName || "SkillsBridge",
        wizardStep: null,
        userRole: profileRole || "user",
        activeRoleResources,
        sessionId: currentSessionId,
        conversationHistory: historyForBackend,
      };

      allieRequestRef.current = callAllieBackend(
        payload,
        {
          onToken: (token) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === botMsgId
                  ? { ...m, content: m.content + token, isTyping: false }
                  : m
              )
            );
          },
          onDone: ({ sessionId: returnedSessionId, messageId: returnedMessageId } = {}) => {
            if (returnedSessionId) {
              setCurrentSessionId(returnedSessionId);
            }
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== botMsgId) return m;
                return {
                  ...m,
                  isStreaming: false,
                  messageId: returnedMessageId,
                };
              })
            );
            setIsStreaming(false);
            track(ALLIE_EVENTS.RESPONSE_RECEIVED, {
              pageContext: pageContext.displayName || location.pathname,
            });
          },
          onError: () => {
            track(ALLIE_EVENTS.RESPONSE_FAILED, {
              pageContext: pageContext.displayName || location.pathname,
            });
            setMessages((prev) =>
              prev.map((m) =>
                m.id === botMsgId
                  ? {
                      ...m,
                      content:
                        "Sorry, I'm having trouble connecting right now. Please try again.",
                      isStreaming: false,
                      isTyping: false,
                    }
                  : m
              )
            );
            setIsStreaming(false);
          },
        }
      );
    },
    [isStreaming, messages, pageContext, profileRole, entitlements, currentSessionId, track]
  );

  const handleStop = useCallback(() => {
    allieRequestRef.current?.abort();
    setMessages((prev) =>
      prev.map((m) => (m.isStreaming ? { ...m, isStreaming: false, isTyping: false, stopped: true } : m))
    );
    setIsStreaming(false);
  }, []);

  const handleSend = useCallback(() => {
    if (!inputValue.trim() || isStreaming) return;
    sendMessage(inputValue);
    setInputValue("");
  }, [inputValue, isStreaming, sendMessage]);

  const handleSuggestionClick = useCallback(
    (suggestion) => sendMessage(suggestion),
    [sendMessage]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Per-message feedback
  // ─────────────────────────────────────────────────────────────────────────────

  const handleMessageFeedback = useCallback(
    async (msgId, sentiment, userText, files = []) => {
      const target = messages.find((m) => m.id === msgId && m.role === "assistant");
      if (!target || !target.content?.trim()) return;

      track(ALLIE_EVENTS.FEEDBACK_SUBMITTED, {
        scope: "message",
        sentiment,
        hasComment: !!userText?.trim(),
        fileCount: files?.length ?? 0,
      });

      const msgIndex = messages.findIndex((m) => m.id === msgId);
      let relatedUserMessage = null;
      if (msgIndex > 0) {
        for (let i = msgIndex - 1; i >= 0; i -= 1) {
          if (messages[i].role === "user") {
            relatedUserMessage = messages[i];
            break;
          }
        }
      }

      const truncate = (str, max) => {
        if (!str) return "";
        return str.length > max ? `${str.slice(0, max - 1)}…` : str;
      };

      const label = sentiment === "up" ? "Thumbs up" : "Thumbs down";
      const autoText = `[Ask Allie] ${label} | Question: "${truncate(
        relatedUserMessage?.content || "",
        240
      )}" | Answer: "${truncate(target.content, 240)}"`;
      const feedbackPayloadText = userText?.trim()
        ? `${userText.trim()} | ${autoText}`
        : autoText;

      // Optimistic UI update
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msgId
            ? { ...m, feedback: sentiment, isFeedbackSubmitting: true, feedbackError: null }
            : m
        )
      );
      setDownPendingMsgId(null);
      setDownReasonText("");
      setDownFiles([]);

      try {
        let payload;
        if (files && files.length > 0) {
          payload = new FormData();
          payload.append("sessionId", currentSessionId || "");
          payload.append("messageId", target.messageId || "");
          payload.append("sentiment", sentiment);
          payload.append("feedbackText", feedbackPayloadText);
          payload.append("pageContext", pageContext.displayName || location.pathname);
          files.forEach((f) => payload.append("files", f));
        } else {
          payload = {
            sessionId: currentSessionId || "",
            messageId: target.messageId || "",
            sentiment,
            feedbackText: feedbackPayloadText,
            pageContext: pageContext.displayName || location.pathname,
          };
        }
        await submitAllieFeedback(payload);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msgId ? { ...m, isFeedbackSubmitting: false } : m
          )
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error submitting Ask Allie feedback:", error);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msgId
              ? { ...m, isFeedbackSubmitting: false, feedbackError: "Failed to send feedback" }
              : m
          )
        );
      }
    },
    [location.pathname, messages, currentSessionId, pageContext, track]
  );

  /** Validate a screenshot/video picked in the thumbs-down box; stash for submit. */
  const handleDownFileSelect = useCallback((file) => {
    if (!FEEDBACK_MEDIA_TYPES.includes(file.type)) {
      SbNotification.error("Invalid File Type", "Only images or videos are allowed.");
      return false;
    }
    if (file.size > FEEDBACK_MAX_SIZE_BYTES) {
      SbNotification.error("File Too Large", `Files must be smaller than ${FEEDBACK_MAX_SIZE_MB}MB.`);
      return false;
    }
    setDownFiles((prev) => {
      const isDuplicate = prev.some((f) => f.name === file.name && f.size === file.size);
      return isDuplicate ? prev : [...prev, file].slice(0, FEEDBACK_MAX_FILES);
    });
    return false; // never auto-upload
  }, []);

  const removeDownFile = useCallback((idx) => {
    setDownFiles((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // Render helpers
  // ─────────────────────────────────────────────────────────────────────────────

  /** Thumbs up/down + inline reason box, shown under each finished assistant reply. */
  const renderMessageFooter = (msg) => {
    if (
      msg.role !== "assistant" ||
      msg.isStreaming ||
      msg.isTyping ||
      msg.stopped ||
      !msg.content?.trim()
    ) {
      return undefined;
    }

    return (
      <div className="sb-chat-feedback">
        <span className="sb-chat-feedback-label">Was this helpful?</span>
        <Flex align="center" gap={4}>
          <SbButton
            size="small"
            buttonType={BUTTON_TYPE.ACTION}
            onClick={() => handleMessageFeedback(msg.id, "up")}
            disabled={msg.isFeedbackSubmitting}
            aria-label="Thumbs up"
            className={`sb-chat-feedback-btn sb-chat-feedback-btn--up${msg.feedback === "up" ? " is-active" : ""}`}
            icon={<ThumbIconUp color={msg.feedback === "up" ? SB_COLORS.primary : SB_COLORS.tertiary} aria-hidden="true" />}
          />
          <SbButton
            size="small"
            buttonType={BUTTON_TYPE.ACTION}
            onClick={() => {
              if (msg.feedback === "down") return;
              setDownPendingMsgId(msg.id);
              setDownReasonText("");
              setDownFiles([]);
            }}
            disabled={msg.isFeedbackSubmitting}
            aria-label="Thumbs down"
            className={`sb-chat-feedback-btn sb-chat-feedback-btn--down${msg.feedback === "down" ? " is-active" : ""}`}
            icon={<ThumbIconDown color={msg.feedback === "down" ? SB_COLORS.danger : SB_COLORS.tertiary} aria-hidden="true" />}
          />
        </Flex>

        {downPendingMsgId === msg.id && (
          <div className="sb-chat-feedback-reason">
            <SbInput
              textarea
              rows={2}
              placeholder="What went wrong? (optional)"
              value={downReasonText}
              onChange={(e) => setDownReasonText(e.target.value)}
              className="sb-chat-feedback-reason-input"
              maxLength={500}
              autoFocus
            />
            {downFiles.length > 0 && (
              <Flex gap={8} wrap="wrap" className="sb-chat-feedback-row">
                {downFiles.map((file, idx) => (
                  <SbTooltip key={`${file.name}-${idx}`} title={file.name} placement="top">
                    <span>
                      <SbTag closable onClose={() => removeDownFile(idx)}>
                        {file.name.length > 18 ? `${file.name.slice(0, 17)}…` : file.name}
                      </SbTag>
                    </span>
                  </SbTooltip>
                ))}
              </Flex>
            )}
            <Flex align="center" gap={8} className="sb-chat-feedback-row">
              <SbButton danger size="small" onClick={() => handleMessageFeedback(msg.id, "down", downReasonText, downFiles)}>
                Submit
              </SbButton>
              <SbButton type="link" size="small" onClick={() => handleMessageFeedback(msg.id, "down", "", [])}>
                Skip
              </SbButton>
              <SbTooltip title="Attach a screenshot or short video (optional)" placement="top">
                <span>
                  <SbUpload
                    beforeUpload={handleDownFileSelect}
                    showUploadList={false}
                    fileList={[]}
                    multiple
                    accept={FEEDBACK_ACCEPT}
                    disabled={downFiles.length >= FEEDBACK_MAX_FILES}
                  >
                    <SbButton
                      size="small"
                      buttonType={BUTTON_TYPE.ACTION}
                      aria-label="Attach screenshot or video"
                      disabled={downFiles.length >= FEEDBACK_MAX_FILES}
                      icon={<FileuploadIcon color={SB_COLORS.primary} />}
                    >
                      Attach
                    </SbButton>
                  </SbUpload>
                </span>
              </SbTooltip>
            </Flex>
          </div>
        )}
      </div>
    );
  };

  // Map internal messages → Bubble.List items
  const bubbleItems = messages.map((msg) => ({
    key: msg.id,
    role: msg.role,
    content: msg.content,
    loading: msg.role === "assistant" && msg.isTyping, // three-dot thinking indicator
    footer: renderMessageFooter(msg),
  }));

  // ── History → Conversations items (grouped by date) ──────────────────────────
  const historyGroups = groupByDate(conversations);
  const groupOrder = historyGroups.map((g) => g.label);
  const groupLabelById = {};
  historyGroups.forEach(({ label, items }) =>
    items.forEach((it) => {
      groupLabelById[it.id] = label;
    })
  );
  const conversationItems = conversations.map((c) => ({
    key: c.id,
    label: c.title,
    group: groupLabelById[c.id],
    timestamp: c.createdAt,
  }));

  // ─────────────────────────────────────────────────────────────────────────────
  // Header (rendered as the SbDrawer title node)
  // ─────────────────────────────────────────────────────────────────────────────

  /** Size toggle: shrink to the bottom-right card, or restore to full size. */
  const renderSizeButton = () => (
    <SbButton
      buttonType={BUTTON_TYPE.ACTION}
      className="sb-header-icon-btn"
      onClick={isCompact ? expandPanel : minimizePanel}
      aria-label={isCompact ? "Expand Ask Allie to full size" : "Minimize Ask Allie"}
      title={isCompact ? "Expand to full size  (Alt+A)" : "Minimize  (Alt+A)"}
      icon={isCompact ? <FullscreenOutlined aria-hidden="true" /> : <MinusOutlined aria-hidden="true" />}
    />
  );

  // The header is the drag handle at both sizes. DraggableCore supplies the
  // gesture, useAllieDrag turns it into the panel's offset.
  const withDragHandle = (header) => (
    <DraggableCore {...draggableProps} nodeRef={headerRef}>
      {header}
    </DraggableCore>
  );

  const renderHeader = () => {
    if (view === PANEL_VIEW.HISTORY) {
      return withDragHandle(
        <div className="sb-assist-header" ref={headerRef} {...dragHandleProps}>
          <Flex align="center" gap={8} flex="1 1 auto" style={{ minWidth: 0 }}>
            <SbButton
              buttonType={BUTTON_TYPE.ACTION}
              className="sb-header-icon-btn"
              onClick={() => setView(PANEL_VIEW.CHAT)}
              aria-label="Back to chat"
              title="Back to chat"
              icon={<ArrowLeftOutlined aria-hidden="true" />}
            />
            <span id="sb-assist-title" className="sb-assist-title">
              Chat History
            </span>
          </Flex>
          <div className="sb-assist-header-actions">
            {renderSizeButton()}
            <SbButton
              buttonType={BUTTON_TYPE.ACTION}
              className="sb-header-icon-btn"
              onClick={handleClose}
              aria-label="Close Ask Allie"
              title="Close"
              icon={<CloseOutlined aria-hidden="true" />}
            />
          </div>
        </div>
      );
    }

    return withDragHandle(
      <div
        className="sb-assist-header sb-assist-header-content"
        ref={headerRef}
        {...dragHandleProps}
      >
        <Flex align="center" gap={8}>
          <span id="sb-assist-title" className="sb-assist-title">
            <StarsIcon className="sb-assist-title-icon me-2" color={SB_COLORS.primary} aria-hidden="true" />
            Ask Allie
            <SbBetaTag />
          </span>
          {activeTab === PANEL_TAB.CHAT && !isCompact && pageContext.displayName && (
            <SbTag className="sb-assist-page-badge" bordered={false}>
              {pageContext.displayName}
            </SbTag>
          )}
          <div className="sb-assist-header-actions ms-auto">
            {activeTab === PANEL_TAB.CHAT && (
              <>
                <SbBadge count={conversations.length} size="small" overflowCount={99}>
                  <SbButton
                    buttonType={BUTTON_TYPE.ACTION}
                    className="sb-header-icon-btn"
                    onClick={handleOpenHistory}
                    aria-label={`View chat history${conversations.length > 0 ? ` (${conversations.length} saved)` : ""}`}
                    title="Chat history"
                    icon={<HistoryOutlined aria-hidden="true" />}
                  />
                </SbBadge>
                <SbButton
                  buttonType={BUTTON_TYPE.ACTION}
                  className="sb-header-icon-btn"
                  onClick={handleNewChat}
                  aria-label="Start new conversation"
                  title="New conversation"
                  icon={<PlusOutlined aria-hidden="true" />}
                />
              </>
            )}
            {renderSizeButton()}
            <SbButton
              buttonType={BUTTON_TYPE.ACTION}
              className="sb-header-icon-btn"
              onClick={handleClose}
              aria-label="Close Ask Allie"
              title="Close"
              icon={<CloseOutlined aria-hidden="true" />}
            />
          </div>
        </Flex>
        {/* Tabs cost a whole row, which the compact card can't spare. */}
        {!isCompact && (
          <SbTabs
            activeKey={activeTab}
            onChange={setActiveTab}
            size="small"
            className="sb-assist-tabs"
            items={[
              { key: PANEL_TAB.CHAT, label: <><StarsIcon className="me-1" aria-hidden="true" />Chat</> },
              { key: PANEL_TAB.FEEDBACK, label: <><FlagOutlined className="me-1" aria-hidden="true" />Feedback</> },
            ]}
          />
        )}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <ConfigProvider
      theme={{
        token: { colorPrimary: SB_COLORS.primary, borderRadius: 8 },
        components: {
          Tabs: { inkBarColor: SB_COLORS.primary, itemSelectedColor: SB_COLORS.primary },
        },
      }}
    >
      <XProvider>
        {/* Trigger FAB — also the restore control for a closed panel; the dot
            marks a conversation still in progress behind it. */}
        {!show && (
          <SbBadge dot={hasActiveConversation} status={isStreaming ? "processing" : "default"}>
            <AskAllieFabStyled
              buttonType="action"
              className="sb-assist-fab"
              onClick={handleOpen}
              title={hasActiveConversation ? "Resume Ask Allie  (Alt+A)" : "Ask Allie  (Alt+A)"}
              aria-label={hasActiveConversation ? "Resume Ask Allie (Alt+A)" : "Open Ask Allie (Alt+A)"}
              icon={<StarsIcon aria-hidden="true" />}
            />
          </SbBadge>
        )}

        <SbDrawer
          open={show}
          onClose={handleClose}
          placement="right"
          width={PANEL_WIDTH[panelSize]}
          mask={false}
          closable={false}
          title={renderHeader()}
          classNames={{ wrapper: "sb-assist-offcanvas" }}
          rootClassName={`sb-assist-offcanvas-root${isCompact ? " is-compact" : ""}${
            isUndocked ? " is-floating" : ""
          }${isDragging ? " is-dragging" : ""}`}
          styles={drawerStyles}
          // Claims antd's focus lock while the user is in Allie, so a masked
          // drawer/modal underneath can't pull the keystrokes back to itself.
          focusable={{ trap: focusTrap }}
          aria-labelledby="sb-assist-title"
        >
          {view === PANEL_VIEW.HISTORY ? (
            /* ── History view ── */
            isHistoryLoading ? (
              <div className="sb-history-empty">
                <SbSpin size="small" aria-label="Loading history" />
                <p className="sb-history-empty-hint mt-2">Loading conversations…</p>
              </div>
            ) : conversations.length === 0 ? (
              <Welcome
                className="sb-history-empty"
                icon={<MessageOutlined aria-hidden="true" />}
                title="No saved conversations"
                description="Your conversations will appear here after you chat with Allie."
              />
            ) : (
              <Conversations
                className="sb-history-body"
                items={conversationItems}
                groupable={{ sort: (a, b) => groupOrder.indexOf(a) - groupOrder.indexOf(b) }}
                onActiveChange={(key) => handleLoadConversation(key)}
                menu={(item) => ({
                  items: [{ key: "delete", label: "Delete", danger: true, icon: <DeleteOutlined /> }],
                  onClick: ({ key }) => {
                    if (key === "delete") handleDeleteConversation(item.key);
                  },
                })}
              />
            )
          ) : activeTab === PANEL_TAB.FEEDBACK ? (
            /* ── Feedback view ── */
            <div className="sb-assist-feedback-body">
              {showFeedbackSuccess ? (
                <SbSuccessAlert
                  show={showFeedbackSuccess}
                  onClose={() => setShowFeedbackSuccess(false)}
                  title="Thank you for your feedback!"
                  message="We're working on it — you will be notified when this is resolved."
                />
              ) : (
                <FeedbackV2
                  onClose={() => setActiveTab(PANEL_TAB.CHAT)}
                  actionFunction={handleFeedbackSubmit}
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  disabled={feedbackLoading}
                  onFileUpload={setFeedbackFiles}
                  loading={feedbackLoading}
                  accept={FEEDBACK_ACCEPT}
                  allowedTypes={FEEDBACK_MEDIA_TYPES}
                  maxSizeMB={FEEDBACK_MAX_SIZE_MB}
                />
              )}
            </div>
          ) : (
            /* ── Chat view ── */
            <>
              <Bubble.List
                ref={bubbleListRef}
                className="sb-chat-messages"
                role={BUBBLE_ROLES}
                items={bubbleItems}
                autoScroll={false}
              />

              {/* Suggestion chips — visible before the first user message */}
              {showSuggestions && (
                <Prompts
                  className="sb-chat-suggestions"
                  wrap
                  items={suggestions.map((s, i) => ({ key: String(i), label: s }))}
                  onItemClick={({ data }) => handleSuggestionClick(data.label)}
                />
              )}

              {/* Input area */}
              <div className="sb-chat-input-area">
                <Sender
                  ref={inputRef}
                  value={inputValue}
                  onChange={(v) => setInputValue(v)}
                  onSubmit={() => handleSend()}
                  onCancel={handleStop}
                  loading={isStreaming}
                  placeholder={isStreaming ? "Type your next question…" : "Ask a question…"}
                  aria-label="Type your message"
                />
                <p className="sb-chat-disclaimer">
                  Ask Allie may make mistakes. Verify with Customer Success.
                </p>
              </div>
            </>
          )}
        </SbDrawer>
      </XProvider>
    </ConfigProvider>
  );
};

export default AskAllie;
